import React, { useEffect, useState, useCallback, useMemo, useContext } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { HvProvider } from '@hitachivantara/uikit-react-core';
import { Router as ReactRouter, Route, Switch, Redirect } from 'react-router-dom';
import { AuthorizationProvider, useAuth } from '@iq/react-components';
import config from '../../config';

import {
  requestGrants,
  getGrants,
  authorizeUser,
  setCurrentUser,
  hasCheckedInvitations,
  getIsTenantOrSuperAdmin,
  getHasPermission,
  hasDocumentationReadPermission,
} from '../../bundles/auth';
import { getActiveSite, requestSites, getSitesLoaded } from '../../bundles/sites';
import { requestInstructionals } from '../../bundles/files';
import { getOnline } from '../../bundles/notifications';
import { requestComponents } from '../../bundles/components';
import { requestVisualizations } from '../../bundles/visualizations';
import { requestModels } from '../../bundles/models';
import { requestStateSets } from '../../bundles/statesets';
import { syncNotificationConfigs } from '../../bundles/notification-config';

import BaseView from '../BaseView';

import SiteType from '../SiteType';
import SiteDetailView from '../SiteDetailView';
import SiteAdminView from '../SiteAdminView';
import LayoutView from '../SiteAdminView/LayoutView';
import IntegrationView from '../SiteAdminView/IntegrationView';
import QrGenerationView from '../QrGenerationView';
import SourceView from '../SiteAdminView/SourceView';
import VariableView from '../SiteAdminView/VariableView';
import ComponentView from '../SiteAdminView/ComponentView';
import AttributeView from '../SiteAdminView/AttributeView';
import VisualizationView from '../SiteAdminView/VisualizationView';
import ModelView from '../SiteAdminView/ModelView';
import MemberView from '../SiteAdminView/MemberView';
import TagsView from '../SiteAdminView/TagsView';
import NotificationLandingView, { TransformerAlarmsView } from '../SiteAdminView/NotificationView';
import SupportView from '../SupportView';
import NoGrantsView from '../NoGrantsView';
import StateSetView from '../SiteAdminView/StateSetView';
import SiteView from '../SiteAdminView/SiteView';
import PageLastUpdated from '../PageHeader/PageLastUpdated';
import SourceConnectivityNotification from '../Notifications/SourceConnectivityNotification';
import GlobalMessagePortal from '../Notifications/GlobalMessagePortal';
import TopNotification from '../Notifications/TopNotification';
import AppContext from '../../AppContext';
import TenantAdminIntegrationView from '../TenantAdminView/components/TenantAdminIntegrationView';
import TenantAdminUserView from '../TenantAdminView/components/TenantAdminUserView/TenantAdminUserView';
import ApiDocumentView from '../ApiDocumentView/ApiDocumentView';
import ApiDocumentOverView from '../ApiDocumentView/ApiDocumentOverView';
import AnalyticsView from '../SiteAdminView/AnalyticsView';

const { AuthProvider } = AuthorizationProvider;

const NotFoundPage = () => (
  <div className="not-found">
    <h2>Sorry, page not found...</h2>
  </div>
);

const removeFirstLoader = () => {
  const loader = document.querySelector('#first-loader');
  if (loader) loader.remove();
};

const Router = ({ history }) => {
  const dispatch = useDispatch();

  const { subject, realm, profile } = useAuth();

  const { email } = profile;
  const [isLoadingSites, setIsLoadingSites] = useState(true);
  const [isLoadingGrants, setIsLoadingGrants] = useState(true);

  const grants = useSelector(getGrants);
  const site = useSelector(getActiveSite);
  const sitesLoaded = useSelector(getSitesLoaded);

  const online = useSelector(getOnline);
  const invitesChecked = useSelector(hasCheckedInvitations);

  const { setTheme, theme } = useContext(AppContext);

  const setIqTheme = useCallback(() => {
    setTheme(theme);
  }, [theme]);

  const hvTheme = useMemo(() => (theme === 'dark' ? 'wicked' : null), [theme]);

  useEffect(() => {
    if (online && isLoadingGrants) {
      dispatch(
        requestGrants(subject, () => {
          setIsLoadingGrants(false);
        })
      );
    }
  }, [online, isLoadingGrants]);

  useEffect(() => {
    if (online && invitesChecked) {
      setIsLoadingGrants(true);
    }
  }, [online, invitesChecked]);

  const isTenantAdmin = useSelector((state) => getIsTenantOrSuperAdmin(state, realm));

  const canReadSites = useSelector((state) => getHasPermission(state, 'sites/Read'));

  const canWriteNotificationConfig = useSelector((state) =>
    site?.id
      ? getHasPermission(state, 'notificationConfig/Write', { org: site.org, site: site.id })
      : false
  );

  const canReadDocumentation = useSelector(hasDocumentationReadPermission);

  useEffect(() => {
    if (online && canReadSites) {
      dispatch(
        requestSites(() => {
          setIsLoadingSites(false);
        })
      );
      dispatch(requestInstructionals());
    }
  }, [online, canReadSites]);

  useEffect(() => {
    if (online && canReadSites && site?.id) {
      dispatch(requestComponents(site.id));
      dispatch(requestVisualizations(site.id));
      dispatch(requestModels(site.id));
      dispatch(requestStateSets(site.id));
      if (!config.onPremise && canWriteNotificationConfig) {
        dispatch(syncNotificationConfigs({ org: site.org, site: site.id }));
      }
    }
  }, [online, canReadSites, canWriteNotificationConfig, site.id, site.org, dispatch]);

  useEffect(() => {
    if (!config.onPremise) {
      dispatch(authorizeUser({ email, id: subject, org: realm }));
    }
    dispatch(setCurrentUser({ email, subject }));
  }, []);

  if (isLoadingGrants || (canReadSites && isLoadingSites)) {
    return (
      <div>
        {!online && (
          <GlobalMessagePortal>
            <TopNotification
              icon="he-nonetwork"
              message="No internet connection"
            />
          </GlobalMessagePortal>
        )}
      </div>
    );
  }

  if (history.location && history.location.pathname !== '/sites/' && sitesLoaded) {
    removeFirstLoader();
  }

  if (invitesChecked && !isLoadingGrants && (!grants || !canReadSites)) {
    removeFirstLoader();
    return <NoGrantsView />;
  }

  return (
    <HvProvider
      uiKitTheme={hvTheme}
      changeTheme={setIqTheme}
    >
      <div className={isLoadingGrants || isLoadingSites ? 'loading-main-content' : 'main-content'}>
        <ReactRouter history={history}>
          <AuthProvider value={{ grants }}>
            <Switch>
              {/* Pre-site routes */}
              <Route
                exact
                path="/"
              >
                <Redirect to="/sites/" />
              </Route>
              <Route
                exact
                path="/sites/"
                render={(properties) => (
                  <SiteType
                    {...properties}
                    sitesReady={!isLoadingSites}
                    realm={realm}
                    noSiteView
                  />
                )}
              />

              <Route
                path="/api-docs/:id"
                render={(properties) => (
                  <BaseView
                    title={{
                      type: 'string',
                      value: 'API Documentation',
                    }}
                    apiDocumentationView
                    noSitesView
                    className="docs-component"
                    {...properties}
                  >
                    <ApiDocumentView {...properties} />
                  </BaseView>
                )}
              />

              <Route
                path="/api-docs"
                render={(properties) =>
                  canReadDocumentation ? (
                    <BaseView
                      title={{
                        type: 'string',
                        value: 'API Documentation',
                      }}
                      noSitesView
                      className="docs-component"
                      apiDocumentationView
                      {...properties}
                    >
                      <ApiDocumentOverView {...properties} />
                    </BaseView>
                  ) : (
                    <Redirect to="/sites/" />
                  )
                }
              />
              {!config.onPremise && (
                <Route
                  path="/tenant-administration/users"
                  render={(properties) =>
                    isTenantAdmin ? (
                      <BaseView
                        title={{ type: 'string', value: 'Tenant Administration - Users' }}
                        noSitesView
                        className="manage-view"
                        isTenantAdminView
                        {...properties}
                      >
                        <TenantAdminUserView {...properties} />
                      </BaseView>
                    ) : (
                      <Redirect to="/sites/" />
                    )
                  }
                />
              )}
              {!config.onPremise && (
                <Route
                  path="/tenant-administration/integrations"
                  render={(properties) =>
                    isTenantAdmin ? (
                      <BaseView
                        title={{ type: 'string', value: 'Tenant Administration - Integrations' }}
                        noSitesView
                        className="manage-view"
                        isTenantAdminView
                        {...properties}
                      >
                        <TenantAdminIntegrationView {...properties} />
                      </BaseView>
                    ) : (
                      <Redirect to="/sites/" />
                    )
                  }
                />
              )}
              {/* Admin - Settings - Data */}
              {!config.onPremise && (
                <Route
                  path="/sites/:id/admin/integrations"
                  render={(properties) => (
                    <BaseView
                      siteAdminView
                      requiredPermission="integrations/Write"
                      trail={['Administration', 'Integrations']}
                      {...properties}
                    >
                      <IntegrationView />
                    </BaseView>
                  )}
                />
              )}
              <Route
                path="/sites/:id/admin/sources"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="sources/Write"
                    trail={['Administration', 'Sources']}
                    {...properties}
                  >
                    <SourceView />
                  </BaseView>
                )}
              />
              <Route
                path="/sites/:id/admin/variables"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="variables/Write"
                    trail={['Administration', 'Signals']}
                    {...properties}
                  >
                    <VariableView />
                  </BaseView>
                )}
              />
              <Route
                path="/sites/:id/admin/components/qr"
                render={(properties) => <QrGenerationView {...properties} />}
              />
              <Route
                path="/sites/:id/admin/components"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="components/Write"
                    trail={['Administration', 'Components']}
                    {...properties}
                  >
                    <ComponentView />
                  </BaseView>
                )}
              />
              <Route
                path="/sites/:id/admin/attributes"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="variables/Write"
                    trail={['Administration', 'Attributes']}
                    {...properties}
                  >
                    <AttributeView />
                  </BaseView>
                )}
              />

              <Route
                path="/sites/:id/admin/statesets"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="statesets/Write"
                    trail={['Administration', 'Statesets']}
                    {...properties}
                  >
                    <StateSetView />
                  </BaseView>
                )}
              />
              <Route
                path="/sites/:id/admin/visualizations"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="visualizations/Write"
                    trail={['Administration', 'Visualizations']}
                    {...properties}
                  >
                    <VisualizationView />
                  </BaseView>
                )}
              />
              <Route
                path="/sites/:id/admin/analytics"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="sources/Write"
                    trail={['Administration', 'Analytics']}
                    {...properties}
                  >
                    <AnalyticsView />
                  </BaseView>
                )}
              />

              {/* Admin - Settings - General routes */}
              <Route
                path="/sites/:id/admin/info"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="sites/Write"
                    trail={['Administration', 'Site']}
                    {...properties}
                  >
                    <SiteView />
                  </BaseView>
                )}
              />
              {!config.onPremise && (
                <Route
                  path="/sites/:id/admin/members"
                  render={(properties) => (
                    <BaseView
                      siteAdminView
                      requiredPermission="members/Write"
                      trail={['Administration', 'Members']}
                      {...properties}
                    >
                      <MemberView />
                    </BaseView>
                  )}
                />
              )}
              {config.onPremise && (
                <Route
                  path="/sites/:id/admin/members"
                  component={NotFoundPage}
                />
              )}
              <Route
                path="/sites/:id/admin/layout/(pages)?/:page?/(sections)?/:section?/(columns)?/:column?/(panels)?/:panel?"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="sites/Write"
                    {...properties}
                  >
                    <LayoutView />
                  </BaseView>
                )}
              />
              {!config.onPremise && (
                <Route
                  path="/sites/:id/admin/models"
                  render={(properties) => (
                    <BaseView
                      siteAdminView
                      requiredPermission="models/Write"
                      trail={['Administration', 'Models']}
                      {...properties}
                    >
                      <ModelView />
                    </BaseView>
                  )}
                />
              )}
              <Route
                path="/sites/:id/admin/tags"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    requiredPermission="tags/Write"
                    trail={['Administration', 'Tags']}
                    {...properties}
                  >
                    <TagsView />
                  </BaseView>
                )}
              />
              {!config.onPremise && (
                <Route
                  path="/sites/:id/admin/notifications/transformer-alarms"
                  render={(properties) => (
                    <BaseView
                      siteAdminView
                      requiredPermission="notificationConfig/Write"
                      trail={['Administration', 'Notifications', 'Transformer Alarms']}
                      {...properties}
                    >
                      <TransformerAlarmsView />
                    </BaseView>
                  )}
                />
              )}
              {!config.onPremise && (
                <Route
                  exact
                  path="/sites/:id/admin/notifications"
                  render={(properties) => (
                    <BaseView
                      siteAdminView
                      requiredPermission="notificationConfig/Write"
                      trail={['Administration', 'Notifications']}
                      {...properties}
                    >
                      <NotificationLandingView />
                    </BaseView>
                  )}
                />
              )}
              {config.onPremise && (
                <Route
                  path="/sites/:id/admin/notifications"
                  component={NotFoundPage}
                />
              )}
              {/* Admin - Settings */}
              <Route
                exact
                path="/sites/:id/admin"
                render={(properties) => (
                  <BaseView
                    siteAdminView
                    site={site}
                    title={{ type: 'string', value: 'Administration' }}
                    className="settings-component"
                    withPadding={false}
                    {...properties}
                  >
                    <SiteAdminView {...properties} />
                  </BaseView>
                )}
              />
              <Route
                path="/sites/:id/admin/*"
                component={NotFoundPage}
              />

              <Route
                path="/sites/:id/support"
                render={(properties) => (
                  <BaseView
                    site={site}
                    title={{ type: 'string', value: 'Support Information' }}
                    headerChildren={<p style={{ whiteSpace: 'nowrap' }}>Version 3.0</p>}
                    className="support-view-component"
                    withPadding={false}
                    withConnectivity={false}
                    {...properties}
                  >
                    <SupportView {...properties} />
                  </BaseView>
                )}
              />

              <Route
                path="/sites/:id/:page?"
                render={(properties) => (
                  <BaseView
                    site={site}
                    withSidebar
                    withRefresh
                    headerChildren={<PageLastUpdated />}
                    className="site-detail-view-component"
                    withPadding={false}
                    showBanner={true}
                    {...properties}
                  >
                    <SiteDetailView
                      {...properties}
                      subject={subject}
                    />
                  </BaseView>
                )}
              />
              <Route component={NotFoundPage} />
            </Switch>
          </AuthProvider>
        </ReactRouter>
        {!online && (
          <GlobalMessagePortal site={site}>
            <TopNotification
              icon="he-nonetwork"
              message="No internet connection"
            />
          </GlobalMessagePortal>
        )}
        <GlobalMessagePortal>{site.id && <SourceConnectivityNotification />}</GlobalMessagePortal>
      </div>
    </HvProvider>
  );
};

export default Router;
